import "./uploadSessionModal.css";

import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import {
  DOWNLOADABLE_FILE_CATEGORIES,
  FILE_TYPE_LABEL,
  SESSION_STATUS,
} from "../constants";
import React, { useEffect, useRef, useState } from "react";
import { datasetToTitleCase, humanFileSize } from "../services/utils";
import {
  deleteUploadSession,
  fetchUploadSession,
  sendAdvancedLiteral,
  updateUploadSession,
} from "../services/backendRequests";
import { fixHeaders, rows } from "../components/dataTable";
import { hasAdminAccess, hasBackofficeAccess } from "../services/roles";

import DownloadFilesDrawer from "../components/downloads/DownloadFilesDrawer";
import PropTypes from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import { isUploadSessionEditable } from "../services/uploadSessionUtils";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";

export const statTable = (stat) => {
  return (
    <Row>
      <Col xs="12" className="px-3">
        <Table striped className="mb-0">
          {fixHeaders("Category", "Count", "Size", "Updated at")}
          <tbody>
            {_.sortBy(stat, ["updated_at"]).map((s) => (
              <React.Fragment key={s.category}>
                {rows(
                  datasetToTitleCase(FILE_TYPE_LABEL[s.category] ?? s.category),
                  s.count,
                  humanFileSize(s.size),
                  moment(s.updated_at).format("YYYY-MM-DD HH:mm:ss")
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

// --- copy helpers -----------------------------------------------------------

const orDash = (v) => (v === 0 || v ? v : "—");

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch (e) {
    // Fall through to the legacy path below (e.g. insecure context).
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
};

// --- presentational pieces --------------------------------------------------

// A pill button that copies a (hidden) value — used for the technical ids so
// the long UUIDs aren't displayed, only copied. The label is the field name;
// the tooltip flips to "Copied!" on click.
// Controlled: the parent tracks which chip is currently "copied" so only one is
// highlighted at a time (rapid clicks just move the highlight, no stuck greens).
const CopyChip = ({ label, value, copied, onCopy }) => (
  <button
    type="button"
    className={`us-copy-chip${copied ? " is-copied" : ""}`}
    data-tooltip-id="tooltip"
    data-tooltip-content={copied ? "Copied!" : "Click to copy"}
    onClick={() => onCopy(label, value)}
  >
    <span className="us-chip-head">
      <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} me-2`} />
      <span className="us-chip-label">{label}</span>
    </span>
    {(value === 0 || value) && <span className="us-chip-id">{value}</span>}
  </button>
);

CopyChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  copied: PropTypes.bool,
  onCopy: PropTypes.func.isRequired,
};

const AttrGrid = ({ pairs, compact }) => (
  <Row className="g-3">
    {pairs.map(([label, value]) => (
      <Col xs="6" key={label}>
        <div className="us-attr-label">{label}</div>
        <div className={`us-attr-value${compact ? " us-attr-value--sm" : ""}`}>
          <span className="text-break">{orDash(value)}</span>
        </div>
      </Col>
    ))}
  </Row>
);

AttrGrid.propTypes = {
  pairs: PropTypes.array.isRequired,
  compact: PropTypes.bool,
};

const SectionCard = ({ title, actions, children }) => (
  <Card className="shadow-sm h-100">
    <CardHeader className="d-flex align-items-center justify-content-between bg-white py-2">
      <span className="green-color fw-bold">{title}</span>
      {actions && (
        <div className="d-flex align-items-center gap-3">{actions}</div>
      )}
    </CardHeader>
    <CardBody className="py-3">{children}</CardBody>
  </Card>
);

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  children: PropTypes.node,
};

const NoData = () => (
  <div className="text-center text-muted py-2">No data available</div>
);

// Statuses a privileged user can set from the modal, in lifecycle order.
const SELECTABLE_STATUS = [
  SESSION_STATUS.Partial,
  SESSION_STATUS.Completed,
  SESSION_STATUS.Synchronized,
];

// Backoffice/admin can override an upload session's status straight from the
// detail modal (e.g. (re)mark it partial / completed / synchronised), even on a
// session that is otherwise locked for the client. Synchronized is admin-only:
// backoffice sees it but cannot set it.
// Front-only guard: UploadSessionsController#update is still open to any
// backoffice user, so this is an ergonomic restriction, not an enforced one.
// Deliberately one-way: backoffice can still move a synchronised session back
// to Partial when needed, only setting Synchronized is reserved to admins.
const StatusEditor = ({ value, isAdmin, onChange }) => (
  <div className="us-status-editor">
    {SELECTABLE_STATUS.map((s) => (
      <button
        key={s}
        type="button"
        disabled={s === SESSION_STATUS.Synchronized && !isAdmin}
        className={`us-status-btn${value === s ? " is-active" : ""}${
          s === SESSION_STATUS.Partial ? " is-partial" : ""
        }`}
        onClick={() => onChange(s)}
      >
        <i
          className={`fa-solid fa-circle me-2 ${
            s === SESSION_STATUS.Partial ? "text-warning" : "text-success"
          }`}
          aria-hidden="true"
        />
        {SESSION_STATUS[s]}
      </button>
    ))}
  </div>
);

StatusEditor.propTypes = {
  value: PropTypes.number,
  isAdmin: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

// --- modal ------------------------------------------------------------------

const UploadSessionModal = (props) => {
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [us, setUS] = useState(null);
  const [error, setError] = useState();
  const [sendingAdvancedLiteral, setSendingAdvancedLiteral] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const copyTimer = useRef(null);

  const handleCopyChip = async (key, value) => {
    await copyToClipboard(String(value));
    setCopiedKey(key);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  const handleKeyEsc = (e) => {
    if (e.keyCode === 27)
      if (drawerOpen) setDrawerOpen(false);
      else props.closeModal();
  };

  useEffect(() => {
    fetchUploadSession(props.us, props.user, true).then((e) => {
      setUS(e);
      document.addEventListener("keydown", handleKeyEsc, false);
    });

    return () => {
      document.removeEventListener("keydown", handleKeyEsc, false);
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setDrawerOpen(false);
    props.closeModal();
  };

  const handleToggleDrawer = () => {
    const opening = !drawerOpen;
    setDrawerOpen(opening);
    if (opening) {
      const modalEl = document.querySelector(".modal");
      if (modalEl) modalEl.scrollTop = 0;
    }
  };

  const handleDeleteUploadSession = (us, user) => {
    if (window.confirm("Do you really want to delete this upload session ?"))
      deleteUploadSession(us, user)
        .then(() => handleCloseModal())
        .catch((err) => setError(err?.message ?? String(err)));
  };

  // Privileged status override: edit locally, then persist on Save. The change
  // isn't saved until the user clicks Save, so remind them with a notification.
  const handleStatusChange = (value) => {
    if (value !== us.status)
      enqueueSnackbar(
        'Don\'t forget to click "Save" to apply the new status.',
        {
          variant: "warning",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          action: (snackbarId) => (
            <span
              className="clickable font-weight-bold"
              onClick={() => closeSnackbar(snackbarId)}
            >
              Dismiss
            </span>
          ),
        }
      );
    setUS({ ...us, status: value });
  };

  const saveStatus = () => {
    updateUploadSession(us, props.user)
      .then(() => handleCloseModal())
      .catch((err) => setError(err?.message ?? String(err)));
  };

  const handleAdvancedLiteral = () => {
    setSendingAdvancedLiteral(true);
    sendAdvancedLiteral(us, props.user)
      .then(() =>
        enqueueSnackbar("Upload session sent to HCC as advanced literal.", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        })
      )
      .catch((err) => setError(err?.message ?? String(err)))
      .finally(() => setSendingAdvancedLiteral(false));
  };

  const hasDownloadableFiles = us?.statistics?.some((s) =>
    DOWNLOADABLE_FILE_CATEGORIES.includes(s.category)
  );

  if (!us) return "";

  const isAdmin = hasAdminAccess(props.user.self);
  const isPrivileged = hasBackofficeAccess(props.user.self);

  const hasStatusChanged = us.status !== props.us?.status;

  const canSendAdvancedLiteral =
    isPrivileged &&
    !hasStatusChanged &&
    us.upload_template?.name === "literal" &&
    [SESSION_STATUS.Completed, SESSION_STATUS.Synchronized].includes(us.status);

  const genericPairs = [
    ["Company", props.company?.name],
    ["Campaign", us.contract_info.name],
    ["Site", us.site.display_name],
    ["Acquisition date", us.acquisition_date],
    ["Created by", us.user],
    ["Created on", moment(us.created_at).format("YYYY-MM-DD HH:mm:ss")],
  ];

  const detailPairs = Object.entries(us.scalar_data ?? {}).map(([k, v]) => [
    datasetToTitleCase(FILE_TYPE_LABEL[k] ?? k),
    v,
  ]);

  const technicalIds = [
    ["Upload session", us.id],
    ["Company", props.company?.id],
    ["Campaign", us.contract_info.id],
    ["Site", us.site.id],
    ["Upload template", us.upload_template?.name],
  ];

  // 3 columns at the top when the privileged "Technical info" card is shown,
  // 2 otherwise — keeps everything above the fold to avoid scrolling.
  const topColWidth = isPrivileged ? "4" : "6";

  return (
    <div>
      <Modal isOpen={props.isOpen} className="upload-session-modal">
        <ModalHeader>Upload session detail</ModalHeader>

        <ModalBody
          style={{ position: "relative", overflow: "hidden", padding: 0 }}
        >
          <div style={{ padding: "1rem" }}>
            {error && <Alert color="danger">{error}</Alert>}

            <Row className="g-3">
              <Col lg={topColWidth}>
                <SectionCard title="Generic information">
                  <div className="us-scroll">
                    <AttrGrid pairs={genericPairs} compact />
                    {isPrivileged && (
                      <>
                        <div className="us-attr-label mt-3">Status</div>
                        <StatusEditor
                          value={us.status}
                          isAdmin={isAdmin}
                          onChange={handleStatusChange}
                        />
                      </>
                    )}
                    <div className="us-attr-label mt-3">Comment</div>
                    <div className="us-attr-value us-attr-value--sm">
                      <span className="text-break">{orDash(us.comment)}</span>
                    </div>
                  </div>
                </SectionCard>
              </Col>

              <Col lg={topColWidth}>
                <SectionCard title="Detail on data">
                  {detailPairs.length > 0 ? (
                    <AttrGrid pairs={detailPairs} />
                  ) : (
                    <NoData />
                  )}
                </SectionCard>
              </Col>

              {isPrivileged && (
                <Col lg={topColWidth}>
                  <SectionCard title="Technical info">
                    <Row className="g-2">
                      {technicalIds.map(([label, value]) => (
                        <Col xs="6" key={label}>
                          <CopyChip
                            label={label}
                            value={value}
                            copied={copiedKey === label}
                            onCopy={handleCopyChip}
                          />
                        </Col>
                      ))}
                    </Row>
                  </SectionCard>
                </Col>
              )}

              <Col lg="12">
                <SectionCard
                  title="Dataset statistics"
                  actions={
                    hasDownloadableFiles && isPrivileged ? (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleToggleDrawer}
                      >
                        <i className="fa-solid fa-download me-1" />
                        {drawerOpen ? "Hide download files" : "Download files"}
                      </Button>
                    ) : null
                  }
                >
                  {us.statistics.length > 0 ? (
                    statTable(us.statistics)
                  ) : (
                    <NoData />
                  )}
                </SectionCard>
              </Col>
            </Row>
          </div>

          {drawerOpen && (
            <DownloadFilesDrawer
              statistics={us.statistics}
              uploadSession={us}
              onClose={() => setDrawerOpen(false)}
              user={props.user}
            />
          )}
        </ModalBody>

        <ModalFooter>
          {isAdmin && (
            <Button
              color="danger"
              className="me-auto"
              onClick={() => handleDeleteUploadSession(us, props.user)}
            >
              Delete
            </Button>
          )}
          {isUploadSessionEditable(us) && !hasStatusChanged && (
            <Button
              color="primary"
              onClick={() => history.push(`/upload/${us.id}`)}
            >
              <i className="fa-solid fa-sm fa-pencil me-2" />
              Edit
            </Button>
          )}
          {isPrivileged && hasStatusChanged && (
            <Button color="primary" onClick={saveStatus}>
              <i
                className="fa-regular fa-sm fa-floppy-disk me-2"
                aria-hidden="true"
              />
              Save
            </Button>
          )}
          {canSendAdvancedLiteral && (
            <Button
              color="primary"
              disabled={sendingAdvancedLiteral}
              onClick={handleAdvancedLiteral}
            >
              <i className="fa fa-sm fa-paper-plane me-2" aria-hidden="true" />
              Advanced literal
            </Button>
          )}
          <Button color="secondary" onClick={() => handleCloseModal()}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    user: state.user,
  };
}

export default connect(mapStateToProps)(UploadSessionModal);

UploadSessionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  company: PropTypes.object,
  us: PropTypes.object,
};
